/*
 * AQUORIX Billing Home
 * File: BillingHomePage.tsx
 * Path: /Users/larrymclean/CascadeProjects/aquorix-frontend/src/features/dashboard/pages/BillingHomePage.tsx
 *
 * Description:
 *   Data-driven Billing Home landing page for pro operators.
 *   Route: /dashboard/billing
 *
 * Author: AQUORIX Team
 * Version: 0.3.0
 * Status: ACTIVE
 *
 * Change Log:
 *   - 2026-06-17: v0.1.0 Initial Billing Home scaffold with operator financial summary,
 *     needs-attention strip, and billing trip list.
 *   - 2026-06-17: v0.2.0 Replace mock rows with real operator-scoped
 *     /api/v1/dashboard/billing/home data.
 *   - 2026-06-17: v0.3.0 Apply approved Billing Home layout while preserving
 *     real backend data and drill-down routing.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../utils/api";
import "./BillingHomePage.css";

type BillingHomeSummary = {
  outstanding_amount?: string | number | null;
  collected_amount?: string | number | null;
  confirmed_unpaid_amount?: string | number | null;
  paid_manual_review_amount?: string | number | null;
  booking_count?: number | null;
};

type BillingHomeRow = {
  booking_id: string | number;
  guest_name?: string | null;
  guest_email?: string | null;
  guest_phone?: string | null;
  booking_status?: string | null;
  payment_status?: string | null;
  payment_currency?: string | null;
  payment_amount?: string | number | null;
  created_at?: string | null;
  ui_status?: string | null;
  participant_count?: number | null;
  item_count?: number | null;
};

type BillingHomeResponse = {
  ok: boolean;
  status: string;
  operator_id: string;
  currency?: string;
  summary?: BillingHomeSummary;
  needs_attention?: BillingHomeRow[];
  rows?: BillingHomeRow[];
};

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: BillingHomeResponse };

function amount(value: string | number | null | undefined) {
  return Number(value || 0);
}

function money(value: string | number | null | undefined, currency: string) {
  return `${currency} ${amount(value).toFixed(2)}`;
}

function labelize(value?: string | null) {
  if (!value) return "unknown";
  return String(value).replace(/_/g, " ");
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function initials(name?: string | null) {
  return String(name || "?")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function statusClass(row: BillingHomeRow) {
  if (row.ui_status === "paid_manual_review") return "billing-pill is-review";
  if (row.payment_status === "paid") return "billing-pill is-paid";
  if (row.payment_status === "unpaid") return "billing-pill is-unpaid";
  return "billing-pill";
}

export default function BillingHomePage() {
  const nav = useNavigate();

  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [filter, setFilter] = useState<"all" | "unpaid" | "paid" | "review">("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadBillingHome() {
      try {
        const res = await apiFetch("/api/v1/dashboard/billing/home", { method: "GET" });
        const json = (await res.json().catch(() => ({}))) as BillingHomeResponse & { message?: string };

        if (!res.ok || json?.ok === false) {
          throw new Error(json?.message || `HTTP ${res.status}`);
        }

        if (mounted) setState({ status: "ok", data: json });
      } catch (err: any) {
        if (mounted) setState({ status: "error", message: err?.message || "Billing Home load failed." });
      }
    }

    loadBillingHome();

    return () => {
      mounted = false;
    };
  }, []);

  const data = state.status === "ok" ? state.data : undefined;
  const currency = data?.currency || "JOD";
  const summary = data?.summary || {};

  const rows = useMemo(() => {
    return data && Array.isArray(data.rows) ? data.rows : [];
  }, [data]);

  const needsAttention = useMemo(() => {
    return data && Array.isArray(data.needs_attention) ? data.needs_attention : [];
  }, [data]);

  const counts = useMemo(() => {
    return {
      all: rows.length,
      unpaid: rows.filter((row) => row.payment_status !== "paid").length,
      paid: rows.filter((row) => row.payment_status === "paid").length,
      review: rows.filter((row) => row.ui_status === "paid_manual_review").length,
    };
  }, [rows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return rows.filter((row) => {
      if (filter === "unpaid" && row.payment_status === "paid") return false;
      if (filter === "paid" && row.payment_status !== "paid") return false;
      if (filter === "review" && row.ui_status !== "paid_manual_review") return false;

      if (!q) return true;

      return [
        row.guest_name,
        row.guest_email,
        row.guest_phone,
        row.booking_id,
        row.ui_status,
        row.booking_status,
        row.payment_status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [rows, filter, search]);

  if (state.status === "loading") {
    return (
      <div className="billing-home">
        <header className="billing-home-header">
          <h1 className="billing-home-title">Billing</h1>
          <p className="billing-home-subtitle">Loading real AQUORIX billing truth…</p>
        </header>
        <section className="billing-card">
          <div className="billing-empty">Loading billing records…</div>
        </section>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="billing-home">
        <header className="billing-home-header">
          <h1 className="billing-home-title">Billing</h1>
          <p className="billing-home-subtitle">Billing Home could not load.</p>
        </header>
        <section className="billing-card">
          <div className="billing-empty">Failed to load Billing Home: {state.message}</div>
        </section>
      </div>
    );
  }

  return (
    <div className="billing-home">
      <header className="billing-home-header">
        <h1 className="billing-home-title">Billing</h1>
        <p className="billing-home-subtitle">
          Money owed to Blue Current Divers — who owes you, how much, and what needs chasing. All amounts {currency}.
        </p>
      </header>

      <section className="billing-kpi-grid">
        <div className="billing-kpi-card is-primary">
          <div className="billing-kpi-label">Outstanding</div>
          <div className="billing-kpi-value">{money(summary.outstanding_amount, currency)}</div>
          <div className="billing-kpi-sub">Across unpaid booking balances</div>
        </div>

        <div className="billing-kpi-card">
          <div className="billing-kpi-label">Collected</div>
          <div className="billing-kpi-value">{money(summary.collected_amount, currency)}</div>
          <div className="billing-kpi-sub">Recorded as paid</div>
        </div>

        <div className="billing-kpi-card">
          <div className="billing-kpi-label">Confirmed Unpaid</div>
          <div className="billing-kpi-value">{money(summary.confirmed_unpaid_amount, currency)}</div>
          <div className="billing-kpi-sub">Confirmed seats awaiting payment</div>
        </div>

        <div className="billing-kpi-card">
          <div className="billing-kpi-label">Manual Review</div>
          <div className="billing-kpi-value">{money(summary.paid_manual_review_amount, currency)}</div>
          <div className="billing-kpi-sub">Paid but not confirmed</div>
        </div>
      </section>

      <section className="billing-card">
        <div className="billing-card-inner">
          <div className="billing-section-label">Needs Attention</div>
        </div>

        {needsAttention.length === 0 ? (
          <div className="billing-empty">No billing records need attention.</div>
        ) : (
          needsAttention.map((row) => (
            <button
              key={`attention-${row.booking_id}`}
              type="button"
              className="billing-attention-row"
              onClick={() => nav(`/dashboard/billing/${row.booking_id}`)}
            >
              <span className={statusClass(row)}>{labelize(row.ui_status)}</span>
              <span>
                <div className="billing-name">{row.guest_name || "Unknown customer"}</div>
                <div className="billing-meta">Booking #{row.booking_id} · Created {formatDate(row.created_at)}</div>
              </span>
              <span>
                <div className="billing-table-head">Booking</div>
                <div className="billing-money">{labelize(row.booking_status)}</div>
              </span>
              <span>
                <div className="billing-table-head">Amount</div>
                <div className="billing-money">{money(row.payment_amount, row.payment_currency || currency)}</div>
              </span>
              <span>›</span>
            </button>
          ))
        )}
      </section>

      <div className="billing-toolbar">
        <div className="billing-tabs">
          <button className={`billing-tab ${filter === "all" ? "is-active" : ""}`} onClick={() => setFilter("all")}>All {counts.all}</button>
          <button className={`billing-tab ${filter === "unpaid" ? "is-active" : ""}`} onClick={() => setFilter("unpaid")}>Unpaid {counts.unpaid}</button>
          <button className={`billing-tab ${filter === "paid" ? "is-active" : ""}`} onClick={() => setFilter("paid")}>Paid {counts.paid}</button>
          <button className={`billing-tab ${filter === "review" ? "is-active" : ""}`} onClick={() => setFilter("review")}>Review {counts.review}</button>
        </div>

        <input
          className="billing-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customer or booking…"
        />
      </div>

      <section className="billing-card">
        <div className="billing-table-header">
          <div className="billing-table-head">Customer / Booking</div>
          <div className="billing-table-head">Status</div>
          <div className="billing-table-head">Booking</div>
          <div className="billing-table-head">Payment</div>
          <div className="billing-table-head">Amount</div>
          <div className="billing-table-head">Age</div>
          <div />
        </div>

        {visibleRows.length === 0 ? (
          <div className="billing-empty">No billing records match this filter.</div>
        ) : (
          visibleRows.map((row) => (
            <button
              key={`billing-row-${row.booking_id}`}
              type="button"
              className="billing-table-row"
              onClick={() => nav(`/dashboard/billing/${row.booking_id}`)}
            >
              <div>
                <div className="billing-name">{initials(row.guest_name)} · {row.guest_name || "Unknown customer"}</div>
                <div className="billing-meta">
                  Booking #{row.booking_id} · {row.participant_count ?? 0} participants · {row.item_count ?? 0} items
                </div>
              </div>
              <div><span className={statusClass(row)}>{labelize(row.ui_status)}</span></div>
              <div className="billing-muted">{labelize(row.booking_status)}</div>
              <div className="billing-muted">{labelize(row.payment_status)}</div>
              <div className="billing-money">{money(row.payment_amount, row.payment_currency || currency)}</div>
              <div className="billing-muted">{formatDate(row.created_at)}</div>
              <div>›</div>
            </button>
          ))
        )}
      </section>
    </div>
  );
}